'use client';

import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import type { Tab, TabGroup } from '../../../store/layoutStore';
import type { LayoutState } from '../../../store/layoutStoreTypes';

type SelectionMode = 'plain' | 'range' | 'toggle';

type ResolveTabSelectionOptions = {
  mode: SelectionMode;
  tabId: string;
  selectionAnchorId: string | null;
  selectedTabIds: ReadonlySet<string>;
  visualTabIds: string[];
};

type ResolveTabSelectionResult = {
  selectedTabIds: Set<string>;
  preserveAnchor: boolean;
};

export function resolveTabSelection({
  mode,
  tabId,
  selectionAnchorId,
  selectedTabIds,
  visualTabIds,
}: ResolveTabSelectionOptions): ResolveTabSelectionResult {
  if (mode === 'plain') {
    return {
      selectedTabIds: new Set(),
      preserveAnchor: false,
    };
  }

  if (mode === 'range' && selectionAnchorId) {
    const anchorIndex = visualTabIds.indexOf(selectionAnchorId);
    const targetIndex = visualTabIds.indexOf(tabId);
    if (anchorIndex >= 0 && targetIndex >= 0) {
      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      return {
        selectedTabIds: new Set(visualTabIds.slice(start, end + 1)),
        preserveAnchor: true,
      };
    }
    return {
      selectedTabIds: new Set(),
      preserveAnchor: false,
    };
  }

  const next = new Set(selectedTabIds);
  if (next.size === 0 && selectionAnchorId && selectionAnchorId !== tabId) {
    next.add(selectionAnchorId);
  }
  if (next.has(tabId)) {
    next.delete(tabId);
  } else {
    next.add(tabId);
  }
  return {
    selectedTabIds: next,
    preserveAnchor: false,
  };
}

type UseTabSelectionOptions = {
  tabs: Tab[];
  allTabs: Tab[];
  detachedTabIds: ReadonlySet<string>;
  tabGroups: TabGroup[];
  getTabsInGroup: (groupId: string, tabs: Tab[]) => Tab[];
  getUngroupedTabs: (tabs: Tab[]) => Tab[];
  setActiveTab: LayoutState['setActiveMainContentTab'];
};

export function useTabSelection({
  tabs,
  allTabs,
  detachedTabIds,
  tabGroups,
  getTabsInGroup,
  getUngroupedTabs,
  setActiveTab,
}: UseTabSelectionOptions) {
  const [selectedTabIds, setSelectedTabIds] = useState<Set<string>>(new Set());
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const existingIds = new Set(
      allTabs.filter((tab) => !detachedTabIds.has(tab.id)).map((tab) => tab.id),
    );
    setSelectedTabIds((previous) => {
      if (previous.size === 0) return previous;
      const next = new Set(
        [...previous].filter((tabId) => existingIds.has(tabId)),
      );
      return next.size === previous.size ? previous : next;
    });
    setSelectionAnchorId((previous) =>
      previous && existingIds.has(previous) ? previous : null,
    );
  }, [allTabs, detachedTabIds]);

  const getVisualTabIds = useCallback((): string[] => {
    const pinnedIds = tabs
      .filter((tab) => tab.isPinned && !tab.groupId)
      .map((tab) => tab.id);
    const groupedIds = tabGroups.flatMap((group) =>
      group.isCollapsed
        ? []
        : getTabsInGroup(group.id, tabs).map((tab) => tab.id),
    );
    const ungroupedIds = getUngroupedTabs(tabs)
      .filter((tab) => !tab.isPinned)
      .map((tab) => tab.id);
    return [...pinnedIds, ...groupedIds, ...ungroupedIds];
  }, [tabs, tabGroups, getTabsInGroup, getUngroupedTabs]);

  const handleSelect = useCallback(
    (tabId: string, event: MouseEvent) => {
      const mode: SelectionMode =
        event.shiftKey && selectionAnchorId
          ? 'range'
          : event.metaKey || event.ctrlKey
            ? 'toggle'
            : 'plain';
      if (mode !== 'plain') {
        event.preventDefault();
      }
      const result = resolveTabSelection({
        mode,
        tabId,
        selectionAnchorId,
        selectedTabIds,
        visualTabIds: getVisualTabIds(),
      });
      setSelectedTabIds(result.selectedTabIds);
      if (!result.preserveAnchor) {
        setSelectionAnchorId(tabId);
      }
      setActiveTab(tabId);
    },
    [getVisualTabIds, selectedTabIds, selectionAnchorId, setActiveTab],
  );

  return {
    selectedTabIds,
    setSelectedTabIds,
    setSelectionAnchorId,
    handleSelect,
  };
}
